import { specialItemFamilyForCode, specialItemFamilyForAccount, findAccount } from './specialItemFamily';

/**
 * iter921i - Detect special-item family from a GL code.
 *
 * Used by `handleApprove` in BankFeedCenterPage when the user manually
 * changes the GL category in Review Needed. If the new GL belongs to a
 * special-item account class (fixed asset / prepayment / loan / investment
 * / loan-receivable), we MUST route the approval through the linkage
 * modal so the user can either start a new tracked item or link to an
 * existing one - instead of silently posting against a generic GL.
 *
 * Strategy:
 *   1. Hard-coded GL-code list (the canonical AkountFly COA codes).
 *      Catches the vast majority of cases in one comparison.
 *   2. Chart-of-accounts name/type lookup (live data) as a safety net
 *      for businesses on custom COA templates where codes differ.
 *
 * @param {string} glCode - The GL account code (e.g. "1700")
 * @param {string} direction - 'inflow' | 'outflow' (changes interpretation
 *                              of liabilities - a loan inflow is "loan
 *                              received", a loan outflow can be repayment)
 * @param {Array<object>} chartOfAccounts - [{ code, name, type }, ...]
 * @returns {string|null} 'fixed_asset' | 'prepayment' | 'loan' |
 *                        'investment' | 'loan_receivable' | null
 */
export function glToFamily(glCode, direction, chartOfAccounts = []) {
  // Sep 2026 - delegates to the single source of truth (utils/specialItemFamily.js,
  // mirror of backend core/special_item_family.py). The chart-of-accounts row
  // decides; hard-coded code ranges are gone (they mis-flagged VAT/PAYE/CIT
  // payables as loans and inventory as investments). Unknown code → null.
  return specialItemFamilyForCode(glCode, chartOfAccounts, { bankFeedOnly: true });
}

export default glToFamily;

/**
 * Sep 2026 - Which special-item family (if any) should the Approve button
 * route through? The GL is authoritative: AI/cached labels (document_type,
 * sub_category) only count when the GL's account class is compatible, so an
 * equity receipt the AI called "loan" no longer opens the Loan modal, while
 * a Loans-Payable GL always does.
 * @returns {'fixed_asset'|'prepayment'|'loan'|'investment'|'loan_receivable'|null}
 */
export function detectSpecialFamily({ glCode, direction, docType, subCategory, chartOfAccounts = [] }) {
  const fromGl = glToFamily(glCode, direction, chartOfAccounts);
  if (fromGl) return fromGl;
  const sc = String(subCategory || '').toLowerCase();
  const dt = String(docType || '').toLowerCase();
  const claimed =
    (dt === 'fixed_asset' || sc === 'fixed_asset_purchase') ? 'fixed_asset'
    : (dt === 'prepayment' || sc === 'supplier_prepayment') ? 'prepayment'
    : (dt === 'loan_receivable' || /loan_given|loan_receivable|loan_to_/.test(sc)) ? 'loan_receivable'
    : (dt === 'investment' || sc === 'investment_made') ? 'investment'
    : (dt === 'loan' || /(^|_)loan_received|loan_taken/.test(sc)) ? 'loan'
    : (dt === 'deferred_revenue' || sc === 'deferred_revenue_candidate') ? 'deferred_revenue'
    : null;
  if (!claimed || !glCode) return claimed;
  const acct = findAccount(glCode, chartOfAccounts);
  if (!acct) return claimed; // unknown GL: nothing to contradict the AI label
  // The GL is known and is NOT a special-item ledger → the AI label is dropped.
  return specialItemFamilyForAccount(acct) ? specialItemFamilyForAccount(acct) : null;
}

/**
 * iter921j - Given a GL code + direction + chart-of-accounts, infer the
 * canonical document_type and sub_category to use when posting. This is
 * the single source of truth for "what does this GL mean".
 *
 * Used by handleApprove() to update ai_sub_category + ai_document_type
 * via /reclassify so the eventual approve posts the CORRECT document
 * type and routes the special-item modal correctly.
 *
 * Mapping rules (direction-aware):
 *
 *   OUTFLOW (debit non-cash GL):
 *     • fixed_asset family       → document=fixed_asset,    sub=fixed_asset_purchase
 *     • prepayment family        → document=prepayment,     sub=supplier_prepayment
 *     • investment family        → document=investment,     sub=investment_made
 *     • loan_receivable family   → document=loan_receivable,sub=loan_given
 *     • loan (payable) family    → document=other_payment,  sub=loan_repayment
 *     • COA type='expense'       → document=bill,           sub=expense
 *     • COA type='asset' other   → document=other_payment,  sub=other_outflow
 *     • default                  → document=other_payment,  sub=expense
 *
 *   INFLOW (credit non-cash GL):
 *     • loan (payable) family    → document=loan,           sub=loan_received
 *     • COA type='revenue'       → document=invoice,        sub=revenue
 *     • COA type='equity'        → document=other_receipt,  sub=capital_contribution
 *     • COA type='liability'     → document=other_receipt,  sub=other_inflow
 *     • COA type='asset'         → document=other_receipt,  sub=other_inflow (refund / transfer)
 *     • default                  → document=other_receipt,  sub=other_inflow
 *
 * @returns {{document_type:string, sub_category:string, family:string|null}}
 */
export function inferDocFromGl(glCode, direction, chartOfAccounts = []) {
  const family = glToFamily(glCode, direction, chartOfAccounts);
  const isInflow = (direction || '').toLowerCase() === 'inflow';
  const acct = (chartOfAccounts || []).find((a) => String(a.code) === String(glCode));
  const acctType = (acct?.type || '').toLowerCase();

  // ─── OUTFLOW ───────────────────────────────────────────────────────────
  if (!isInflow) {
    if (family === 'fixed_asset')      return { document_type: 'fixed_asset',     sub_category: 'fixed_asset_purchase', family };
    if (family === 'prepayment')       return { document_type: 'prepayment',      sub_category: 'supplier_prepayment',  family };
    if (family === 'investment')       return { document_type: 'investment',      sub_category: 'investment_made',      family };
    if (family === 'loan_receivable')  return { document_type: 'loan_receivable', sub_category: 'loan_given',           family };
    if (family === 'loan')             return { document_type: 'other_payment',   sub_category: 'loan_repayment',       family };
    if (family === 'deferred_revenue') return { document_type: 'other_payment',   sub_category: 'deferred_revenue_refund', family };
    // iter921k - Owner's drawings (equity outflow) is NOT a Special Item.
    // The owner is withdrawing money from the business. Posts as:
    //   DR Owner's Drawings (3300) / Share Capital   CR Bank
    // No tracked-item row needed; family=null.
    if (acctType === 'equity')         return { document_type: 'other_payment',   sub_category: 'owner_drawing',        family: null };
    if (acctType === 'expense')        return { document_type: 'bill',            sub_category: 'expense',              family: null };
    if (acctType === 'asset')          return { document_type: 'other_payment',   sub_category: 'other_outflow',        family: null };
    return                                     { document_type: 'other_payment',  sub_category: 'expense',              family: null };
  }

  // ─── INFLOW ────────────────────────────────────────────────────────────
  if (family === 'loan')               return { document_type: 'loan',            sub_category: 'loan_received',        family };
  if (family === 'deferred_revenue')   return { document_type: 'deferred_revenue', sub_category: 'deferred_revenue_candidate', family };
  if (acctType === 'revenue' || acctType === 'income') {
    return                                     { document_type: 'invoice',        sub_category: 'revenue',              family: null };
  }
  // iter921k - Equity inflows are owner's capital contribution. They are
  // NOT a Special-Item investment (which would mean the business invested
  // in another entity's assets). They post as a simple journal:
  // DR Bank  CR Owner's Equity / Share Capital. family=null so the
  // Special-Item linkage modal does NOT open.
  if (acctType === 'equity')           return { document_type: 'other_receipt',   sub_category: 'capital_contribution', family: null };
  // Liability/asset inflows that don't match a loan → generic other_receipt
  return                                       { document_type: 'other_receipt',  sub_category: 'other_inflow',         family: null };
}
